import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { suggestedCurrencies, allCurrencies, getCurrencyByCode } from '../data/currencies';

const CurrencySelector = ({
  currentCurrency,
  onCurrencyChange,
  buttonClassName = "",
  showButton = true,
  defaultCurrencyCode = null // Pass accommodationDetail.CurrencyCode as default
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef(null);
  const buttonRef = useRef(null);
  const isMountedRef = useRef(true);

  // Ensure component is mounted before rendering portal
  useEffect(() => {
    setMounted(true);
    isMountedRef.current = true;
    return () => {
      // Close modal before unmounting to prevent portal errors
      isMountedRef.current = false;
      setIsModalOpen(false);
      setMounted(false);
    };
  }, []);

  // Use all currencies directly (no filtering)
  const filteredSuggested = suggestedCurrencies;
  const filteredAll = allCurrencies;

  // Close modal when clicking outside
  useEffect(() => {
    if (!isModalOpen || !mounted) return;

    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsModalOpen(false);
      }
    };

    // Check if document.body exists before adding listeners
    if (typeof document !== 'undefined' && document.body) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousedown', handleClickOutside);
        if (document.body) {
          document.body.style.overflow = 'unset';
        }
      }
    };
  }, [isModalOpen, mounted]);

  // Close modal on Escape key
  useEffect(() => {
    if (!isModalOpen || !mounted) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isModalOpen, mounted]);

  // Close modal on viewport/resize changes (when switching desktop/mobile)
  useEffect(() => {
    if (!isModalOpen || !mounted) return;

    let resizeTimer;
    const handleResize = () => {
      // Close modal when viewport changes significantly (desktop to mobile or vice versa)
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (isMountedRef.current) {
          setIsModalOpen(false);
        }
      }, 100);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(resizeTimer);
      };
    }
  }, [isModalOpen, mounted]);

  const handleCurrencySelect = (currency) => {
    onCurrencyChange(currency);
    setIsModalOpen(false);
  };

  // Get current currency - prioritize API currency, ensure it matches the currency list
  // Fallback to defaultCurrencyCode (from accommodationDetail.CurrencyCode) if no currentCurrency
  const currentCurr = (() => {
    if (!currentCurrency) {
      // Use defaultCurrencyCode from prop (accommodationDetail.CurrencyCode) or fallback
      return getCurrencyByCode(defaultCurrencyCode || '');
    }


    // If currentCurrency has a code, try to match it with the currency list
    if (currentCurrency.code) {
      const currencyCode = String(currentCurrency.code).toUpperCase().trim();
      const matchedCurrency = getCurrencyByCode(currencyCode);
      
      
      // If found in list, merge API currency data with list currency (preserve API symbol/name if better)
      if (matchedCurrency && matchedCurrency.code && matchedCurrency.code.toUpperCase() === currencyCode) {
        const result = {
          ...matchedCurrency,
          // Use API currency symbol if available, otherwise use list symbol
          symbol: currentCurrency.symbol || matchedCurrency.symbol,
          // Use API currency name if available, otherwise use list name
          name: currentCurrency.name || matchedCurrency.name,
          // Use API rate if available
          rate: currentCurrency.rate || matchedCurrency.rate
        };
        return result;
      }
      // If not found in list, return the passed currency as-is (from API)
      // Ensure it has all required properties
      const result = {
        code: currentCurrency.code,
        symbol: currentCurrency.symbol || currentCurrency.code,
        name: currentCurrency.name || currentCurrency.code,
        rate: currentCurrency.rate || 1
      };
      return result;
    }
    
    // If it's just a string code, get the full currency object
    if (typeof currentCurrency === 'string') {
      return getCurrencyByCode(currentCurrency);
    }
    
    // Fallback - use defaultCurrencyCode from prop (accommodationDetail.CurrencyCode)
    return getCurrencyByCode(defaultCurrencyCode || '');
  })();
  

  return (
    <>
      {showButton && (
        <button
          ref={buttonRef}
          onClick={() => setIsModalOpen(true)}
          className={buttonClassName || "flex items-center space-x-1 px-2 py-1 bg-white bg-opacity-20 rounded-lg text-xs font-medium hover:bg-opacity-30 transition-colors notranslate"}
          translate="no"
          dir="ltr"
        >
          <span className="text-white" style={{ display: 'inline-block' }}>
            {currentCurr?.symbol || currentCurr?.code || ''}
          </span>
          {/* Show code only when it differs from the symbol (avoids "EGP EGP") */}
          {currentCurr?.code &&
            String(currentCurr.symbol || '').toUpperCase().trim() !==
              String(currentCurr.code).toUpperCase().trim() && (
            <span className="text-white" style={{ display: 'inline-block' }}>
              {currentCurr.code}
            </span>
          )}
        </button>
      )}

      {isModalOpen && mounted && isMountedRef.current && typeof document !== 'undefined' && document.body && document.body.parentNode && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ 
            zIndex: 10000,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
            }
          }}
        >
          <div
            ref={modalRef}
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-fadeIn relative notranslate"
            style={{ zIndex: 10001, direction: 'ltr' }}
            onClick={(e) => e.stopPropagation()}
            translate="no"
            dir="ltr"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 notranslate" translate="no">
              <h2 className="text-xl font-semibold text-gray-900 notranslate" translate="no" id="currency-selector-title">
                Select your currency
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-full"
                aria-label="Close currency selector"
                type="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="m13.31 12 6.89-6.89a.93.93 0 1 0-1.31-1.31L12 10.69 5.11 3.8A.93.93 0 0 0 3.8 5.11L10.69 12 3.8 18.89a.93.93 0 0 0 1.31 1.31L12 13.31l6.89 6.89a.93.93 0 1 0 1.31-1.31z"></path>
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Description text */}
              <div className="mb-4 text-sm text-gray-500 notranslate" translate="no">
                Prices will be displayed in the selected currency. The actual payment currency may vary.
              </div>

              {filteredSuggested.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 notranslate" translate="no" id="picker_title_0">
                    Popular
                  </h3>
                  <ul 
                    aria-labelledby="picker_title_0" 
                    className="grid grid-cols-3 gap-2 notranslate" 
                    translate="no"
                  >
                    {filteredSuggested.map((currency) => {
                      // Case-insensitive comparison for currency codes
                      const currentCode = currentCurr?.code ? String(currentCurr.code).toUpperCase().trim() : '';
                      const currencyCode = currency?.code ? String(currency.code).toUpperCase().trim() : '';
                      const isSelected = currentCode && currencyCode && currentCode === currencyCode;
                      
                      return (
                      <li key={currency.code} className="list-none">
                        <button
                          onClick={() => handleCurrencySelect(currency)}
                          type="button"
                          className={`w-full text-left px-3 py-2.5 rounded-md transition-all flex flex-col items-start relative border ${
                            isSelected
                              ? 'bg-blue-50 border-blue-200 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          aria-current={isSelected ? 'true' : 'false'}
                        >
                          <div className="flex flex-col items-start w-full notranslate" translate="no">
                            <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                              {currency.name}
                            </span>
                            <span className={`text-xs mt-0.5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                              {currency.code}
                            </span>
                          </div>
                          {isSelected && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="14" height="14" fill="currentColor" className="text-blue-600 absolute top-2 right-2">
                              <path d="M56.33 100a4 4 0 0 1-2.82-1.16L20.68 66.12a4 4 0 1 1 5.64-5.65l29.57 29.46 45.42-60.33a4 4 0 1 1 6.38 4.8l-48.17 64a4 4 0 0 1-2.91 1.6z"></path>
                            </svg>
                          )}
                        </button>
                      </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {filteredAll.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 notranslate" translate="no" id="picker_title_1">
                    All Currencies
                  </h3>
                  <ul 
                    aria-labelledby="picker_title_1" 
                    className="grid grid-cols-3 gap-2 notranslate" 
                    translate="no"
                  >
                    {filteredAll.map((currency) => {
                      // Case-insensitive comparison for currency codes
                      const currentCode = currentCurr?.code ? String(currentCurr.code).toUpperCase().trim() : '';
                      const currencyCode = currency?.code ? String(currency.code).toUpperCase().trim() : '';
                      const isSelected = currentCode && currencyCode && currentCode === currencyCode;
                      
                      return (
                      <li key={currency.code} className="list-none">
                        <button
                          onClick={() => handleCurrencySelect(currency)}
                          type="button"
                          className={`w-full text-left px-3 py-2.5 rounded-md transition-all flex flex-col items-start relative border ${
                            isSelected
                              ? 'bg-blue-50 border-blue-200 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          aria-current={isSelected ? 'true' : 'false'}
                        >
                          <div className="flex flex-col items-start w-full notranslate" translate="no">
                            <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                              {currency.name}
                            </span>
                            <span className={`text-xs mt-0.5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                              {currency.code}
                            </span>
                          </div>
                          {isSelected && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="14" height="14" fill="currentColor" className="text-blue-600 absolute top-2 right-2">
                              <path d="M56.33 100a4 4 0 0 1-2.82-1.16L20.68 66.12a4 4 0 1 1 5.64-5.65l29.57 29.46 45.42-60.33a4 4 0 1 1 6.38 4.8l-48.17 64a4 4 0 0 1-2.91 1.6z"></path>
                            </svg>
                          )}
                        </button>
                      </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CurrencySelector;

